import { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Divider,
  Row,
  Col,
} from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createChangeOrderReview, updateChangeOrderReview } from "../../services/contractsAPI";
import { useAuth } from "../../hooks/useAuth";
import AttachmentsSection from "../AttachmentsSection";
import { getChangeOrderLabel, isTimeChangeOrdersEnabled, formatDurationDays } from "../../utils/projectUtils";

const { TextArea } = Input;

const ChangeOrderReviewModal = ({
  visible,
  onClose,
  changeOrderData,
  existingReview,
  isUpdate = false,
  onSuccess,
}) => {
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject.project_id;
  const coLabel = getChangeOrderLabel(selectedProject);
  const timeChangeOrdersEnabled = isTimeChangeOrdersEnabled(selectedProject);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const reviewStatuses = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Revise and Resubmit", value: "revise_and_resubmit" },
  ];

  const createMutation = useMutation({
    mutationFn: async (reviewData) => {
      return await createChangeOrderReview({
        contractId: changeOrderData.contract_id,
        changeOrderId: changeOrderData._id,
        projectId,
        reviewData,
      });
    },
    onSuccess: (data) => {
      message.success("Review submitted successfully!");
      queryClient.invalidateQueries([
        "changeOrderReviews",
        changeOrderData._id,
      ]);
      onSuccess?.(data);
      onClose();
      form.resetFields();
    },
    onError: (error) => {
      message.error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to submit review"
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (reviewData) => {
      return await updateChangeOrderReview({
        contractId: changeOrderData.contract_id,
        changeOrderId: changeOrderData._id,
        projectId,
        reviewId: existingReview._id,
        reviewData,
      });
    },
    onSuccess: (data) => {
      message.success("Review updated successfully!");
      queryClient.invalidateQueries([
        "changeOrderReviews",
        changeOrderData._id,
      ]);
      onSuccess?.(data);
      onClose();
      form.resetFields();
    },
    onError: (error) => {
      message.error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to update review"
      );
    },
  });

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        status: existingReview?.status || "pending",
        notes: existingReview?.notes || "",
        attachments: existingReview?.attachments || [],
      });
    }
  }, [visible, existingReview, form]);

  const handleSubmit = (values) => {
    const reviewData = {
      status: values.status,
      notes: values.notes || null,
      attachments: values.attachments || [],
      reviewer_id: currentUser.id,
      created_at: existingReview?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    if (isUpdate) {
      updateMutation.mutate(reviewData);
    } else {
      createMutation.mutate(reviewData);
    }
  };

  return (
    <Modal
      title={`${isUpdate ? "Update" : "Add"} ${coLabel} Review`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <div className="mb-4">
        <p>
          <strong>{coLabel}:</strong> {changeOrderData?.title}
        </p>
        <p>
          <strong>Number:</strong> {changeOrderData?.number || "N/A"}
        </p>
        <p>
          <strong>Amount:</strong>{" "}
          {changeOrderData?.amount ? `$${changeOrderData.amount}` : "N/A"}
        </p>
        {timeChangeOrdersEnabled && (
          <p>
            <strong>Days:</strong> {formatDurationDays(changeOrderData?.days)}
          </p>
        )}
      </div>

      <Divider />

      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Review Status"
              name="status"
              rules={[
                {
                  required: true,
                  message: "Please select a review status!",
                },
              ]}
            >
              <Select placeholder="Select review status">
                {reviewStatuses.map((status) => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Notes" name="notes">
              <TextArea
                rows={3}
                placeholder="Add your review notes..."
                maxLength={1000}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="attachments"
              label="Attachments"
              className="w-full"
            >
              <AttachmentsSection projectId={projectId} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button onClick={onClose} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isUpdate ? updateMutation.isPending : createMutation.isPending}
            >
              {isUpdate ? "Update Review" : "Submit Review"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ChangeOrderReviewModal;
